import type { ReactNode } from 'react';

/**
 * Piezas de interfaz compartidas.
 *
 * Dos reglas que se aplican en todas ellas:
 *
 * 1. Un dato ausente se muestra como «no disponible» con su motivo, nunca como
 *    cero. Un 0 afirma que se ha medido; un hueco admite que no se sabe.
 * 2. El estado nunca se comunica solo por color: siempre color + icono + texto,
 *    para que sea legible sin distinguir colores y en una captura en blanco y
 *    negro.
 */

export type Tone = 'neutral' | 'good' | 'warn' | 'bad';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'text-[var(--color-ink)]',
  good: 'text-emerald-700 dark:text-emerald-400',
  warn: 'text-amber-700 dark:text-amber-400',
  bad: 'text-red-700 dark:text-red-400',
};

const TONE_ICON: Record<Tone, string> = {
  neutral: '',
  good: '●',
  warn: '▲',
  bad: '■',
};

export function KpiCard({
  label,
  value,
  detail,
  unavailableReason,
  tone = 'neutral',
  href,
}: {
  label: string;
  value: number | null;
  detail?: string;
  unavailableReason?: string;
  tone?: Tone;
  href?: string;
}): JSX.Element {
  const unavailable = value === null;
  const body = (
    <>
      <div className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
        {label}
      </div>
      {unavailable ? (
        <div className="mt-1 text-[15px] font-medium text-[var(--color-ink-muted)]">
          no disponible
        </div>
      ) : (
        <div
          className={`mt-1 flex items-baseline gap-1.5 text-2xl font-semibold ${TONE_CLASS[tone]}`}
        >
          {tone !== 'neutral' && <span className="text-[11px]">{TONE_ICON[tone]}</span>}
          {value.toLocaleString('es-ES')}
        </div>
      )}
      {(detail ?? unavailableReason) && (
        <div className="mt-1 text-[11px] leading-snug text-[var(--color-ink-muted)]">
          {unavailable ? unavailableReason : detail}
        </div>
      )}
    </>
  );

  const className =
    'block rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-3 py-2.5' +
    (href && !unavailable ? ' transition-colors hover:border-[var(--color-ink-muted)]' : '');

  return href && !unavailable ? (
    <a href={href} className={className}>
      {body}
    </a>
  ) : (
    <div className={className}>{body}</div>
  );
}

/** Semáforo accesible: color + icono + texto, nunca solo color. */
export function HealthBadge({
  health,
  reasons,
}: {
  health: 'ok' | 'warn' | 'bad';
  reasons: string[];
}): JSX.Element {
  const map = {
    ok: { tone: 'good' as Tone, label: 'Correcto' },
    warn: { tone: 'warn' as Tone, label: 'Atención' },
    bad: { tone: 'bad' as Tone, label: 'Problema' },
  }[health];

  return (
    <span
      className={`inline-flex items-center gap-1 text-[12px] font-medium ${TONE_CLASS[map.tone]}`}
      title={reasons.length > 0 ? reasons.join(' · ') : 'Sin incidencias detectadas'}
    >
      <span className="text-[9px]">{TONE_ICON[map.tone]}</span>
      {map.label}
    </span>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: Tone;
}): JSX.Element {
  const background = {
    neutral: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]',
    good: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    warn: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300',
    bad: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
  }[tone];
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${background}`}>
      {children}
    </span>
  );
}

/** Valor que puede no existir. Centraliza la regla de «no disponible». */
export function Value({
  children,
  reason = 'sin datos',
}: {
  children: number | string | null | undefined;
  reason?: string;
}): JSX.Element {
  if (children === null || children === undefined) {
    return (
      <span className="text-[var(--color-ink-muted)]" title={reason}>
        —
      </span>
    );
  }
  return <>{typeof children === 'number' ? children.toLocaleString('es-ES') : children}</>;
}

export function RelativeTime({ iso }: { iso: string | null }): JSX.Element {
  if (!iso) return <Value>{null}</Value>;
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  const text =
    days === 0
      ? 'hoy'
      : days === 1
        ? 'ayer'
        : days < 30
          ? `hace ${days} d`
          : days < 365
            ? `hace ${Math.floor(days / 30)} m`
            : `hace ${Math.floor(days / 365)} a`;
  return (
    <span
      title={date.toLocaleString('es-ES')}
      className={days > 30 ? 'text-[var(--color-ink-muted)]' : ''}
    >
      {text}
    </span>
  );
}

export function ProgressBar({ pct }: { pct: number | null }): JSX.Element {
  if (pct === null) {
    return (
      <span
        className="text-[11px] text-[var(--color-ink-muted)]"
        title="El progreso se calcula a partir de las tareas del portal, que llegan en H6"
      >
        sin datos
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-[var(--color-border-subtle)]">
        <span className="block h-full bg-emerald-600" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-[11px] tabular-nums">{pct} %</span>
    </span>
  );
}

export function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="mb-8">
      <div className="mb-2 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold">{title}</h2>
          {subtitle && <p className="text-[12px] text-[var(--color-ink-muted)]">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Table({ head, children }: { head: string[]; children: ReactNode }): JSX.Element {
  return (
    // El contenedor scrollea en horizontal por sí mismo: el cuerpo de la página
    // nunca debe hacerlo.
    <div className="overflow-x-auto rounded-lg border border-[var(--color-border-subtle)]">
      <table className="w-full min-w-[720px] border-collapse text-[13px]">
        <thead>
          <tr className="bg-[var(--color-surface-muted)] text-left">
            {head.map((cell) => (
              <th
                key={cell}
                className="whitespace-nowrap border-b border-[var(--color-border-subtle)] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-border-subtle)] px-4 py-6 text-center text-[13px] text-[var(--color-ink-muted)]">
      {children}
    </div>
  );
}

export function Loading(): JSX.Element {
  return (
    <div className="py-8 text-center text-[13px] text-[var(--color-ink-muted)]">Cargando…</div>
  );
}

export function ErrorBox({ error }: { error: unknown }): JSX.Element {
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
      <strong className="font-semibold">No se pudieron cargar los datos.</strong>{' '}
      {error instanceof Error ? error.message : String(error)}
    </div>
  );
}
